/**
 * Every distinct kind of notification the application produces, in one place.
 *
 * Defined once here rather than left implicit in each `notify()` call site, so
 * the admin master-switch panel, the per-person preference panel, and the
 * server's own gating logic all enumerate the same things and cannot drift out
 * of step with what the code actually sends.
 */
export const NOTIFICATION_CATEGORIES = [
    "digest",
    "task.assigned",
    "task.commented",
    "task.updated",
    "task.mentioned",
    "task.due",
    "task.approval",
    "form.submitted",
    "doc.mentioned",
    "message.mentioned",
    "message.received",
    "thread.reply",
    "message.reminder",
    "project.added",
    "project.status_update",
    "event.invited",
    "event.updated",
    "event.reminder",
    "checkin.reminder",
    "checkin.escalation",
    "report.weekly",
    "automation",
];
export const NOTIFICATION_CATEGORY_LABEL = {
    digest: "Daily digest",
    "task.assigned": "Assigned a task",
    "task.commented": "Comment on a task you follow",
    "task.updated": "Change to a task you follow",
    "task.mentioned": "Mentioned in a task comment",
    "task.due": "Task due or overdue",
    "task.approval": "Approval requested or decided",
    "form.submitted": "Request submitted through a project form",
    "doc.mentioned": "Mentioned in a project doc",
    "message.mentioned": "Mentioned in a message",
    "message.received": "New message in a channel",
    "thread.reply": "Reply in a thread you follow",
    "message.reminder": "A message reminder you set",
    "project.added": "Added to a project",
    "project.status_update": "Project status update",
    "event.invited": "Invited to an event",
    "event.updated": "Event time or details changed",
    "event.reminder": "Reminder before an event starts",
    "checkin.reminder": "Daily check-in reminder",
    "checkin.escalation": "Someone you manage has missed check-ins",
    "report.weekly": "Weekly manager report",
    automation: "Message from a project automation",
};
export const NOTIFICATION_CATEGORY_DESCRIPTION = {
    digest: "The morning summary of overdue and due-today work.",
    "task.assigned": "Someone assigns a task to you.",
    "task.commented": "Someone comments on a task you follow.",
    "task.updated": "A task you follow changes status, due date or owner, is completed, or is unblocked.",
    "task.mentioned": "Someone @mentions you in a task comment.",
    "task.due": "One of your tasks is due today or overdue.",
    "task.approval": "Someone asks you to approve a task, or an approver decides on a task you own or requested.",
    "form.submitted": "Someone files a request through a form on a project you own.",
    "doc.mentioned": "Someone @mentions you in a project doc page.",
    "message.mentioned": "Someone @mentions you in a channel.",
    "message.received": "A new message arrives in a channel you are in.",
    "thread.reply": "Someone replies in a thread you follow (you follow threads you reply in or are mentioned in).",
    "message.reminder": "A reminder you set on a message comes due.",
    "project.added": "Someone adds you to a project.",
    "project.status_update": "A project you are on gets a weekly status update, or yours is due.",
    "event.invited": "Someone adds you to an event on the calendar.",
    "event.updated": "An event you are attending changes time, location, or details.",
    "event.reminder": "An event you are attending is about to start (lead time is set per event).",
    "checkin.reminder": "You are marked as required to check in and have not yet today.",
    "checkin.escalation": "Someone you manage has missed several consecutive workdays without checking in.",
    "report.weekly": "Monday morning: overdue work by person, blockers, projects at risk and missed check-ins for the people and projects you manage.",
    automation: "A rule set up on a project sends you a message, for example when a task you own is about to be due.",
};
/** Every category on for both channels — the state before anyone has touched
 * a single switch, whether that is the workspace default or a person's own. */
export function defaultNotificationCategoryPreferences() {
    return Object.fromEntries(NOTIFICATION_CATEGORIES.map((category) => [category, { inApp: true, email: true }]));
}
//# sourceMappingURL=notification-categories.js.map